import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import UpdateIcon from "@mui/icons-material/Update";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { Link as RouterLink } from "react-router-dom";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const LAST_UPDATED = "February 11, 2026";

const TERMS_SECTIONS = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    paragraphs: [
      "These Terms of Use govern your access to and use of the Infrablaze website and related hosted services made available by Infrablaze.",
      "By accessing or using Infrablaze, you agree to be bound by these Terms and our Privacy Policy. If you are accepting on behalf of an organization, you represent that you are authorized to bind that organization.",
    ],
  },
  {
    id: "service",
    title: "Service Description",
    paragraphs: [
      "Infrablaze provides cloud infrastructure discovery, codification, policy enforcement, and related automation capabilities for internal business operations.",
      "Service functionality may evolve over time, including additions, removals, or changes to features.",
    ],
  },
  {
    id: "changes",
    title: "Changes to Terms",
    paragraphs: [
      "We may update these Terms from time to time. Updates become effective when posted unless a later date is stated.",
      "Continued use of the service after updates are posted constitutes acceptance of the revised Terms.",
    ],
  },
  {
    id: "eligibility",
    title: "Eligibility and Accounts",
    paragraphs: [
      "You must be legally capable of entering binding contracts. Accounts must be registered with accurate and current information.",
      "You are responsible for safeguarding credentials and for activity under your account, including activity by authorized team members.",
    ],
  },
  {
    id: "license",
    title: "Subscription and License",
    paragraphs: [
      "Subject to these Terms and applicable order terms, Infrablaze grants a limited, non-exclusive, non-transferable, and revocable right to access and use the service during the subscription term.",
      "Use is limited to your internal business purposes unless otherwise agreed in writing.",
    ],
  },
  {
    id: "restrictions",
    title: "Acceptable Use Restrictions",
    paragraphs: ["You may not:"],
    bullets: [
      "Reverse engineer, decompile, or attempt to extract source code, except where prohibited by law.",
      "Resell, sublicense, or provide service-bureau access without written authorization.",
      "Bypass security controls, interfere with service integrity, or introduce malicious code.",
      "Use the service to violate law, third-party rights, or contractual obligations.",
      "Use service outputs to build a competing product using Infrablaze confidential implementation details.",
    ],
  },
  {
    id: "customer-data",
    title: "Customer Content and Data",
    paragraphs: [
      "You retain ownership of data, configurations, and content you submit to Infrablaze.",
      "You grant Infrablaze the limited rights needed to host, process, secure, and operate the service for you.",
      "You are responsible for obtaining all rights and permissions needed for data you provide and integrations you enable.",
    ],
  },
  {
    id: "ip",
    title: "Intellectual Property",
    paragraphs: [
      "Infrablaze and its licensors retain all right, title, and interest in the platform, software, documentation, branding, and related intellectual property.",
      "No ownership rights are transferred under these Terms.",
    ],
  },
  {
    id: "third-party",
    title: "Third-Party Services",
    paragraphs: [
      "The service may interoperate with third-party platforms and APIs. Your use of third-party services is subject to those providers' terms and policies.",
      "Infrablaze is not responsible for third-party products, service interruptions, or third-party data handling practices.",
    ],
  },
  {
    id: "feedback",
    title: "Feedback",
    paragraphs: [
      "If you provide suggestions or feedback, you grant Infrablaze a perpetual, worldwide, irrevocable, royalty-free license to use and incorporate that feedback without restriction or obligation.",
    ],
  },
  {
    id: "security",
    title: "Security and Availability",
    paragraphs: [
      "Infrablaze uses commercially reasonable administrative, technical, and organizational safeguards.",
      "No system is fully immune from outages or security incidents; you are responsible for your own backup and business continuity controls where required.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy",
    paragraphs: [
      "Use of personal data is governed by the Infrablaze Privacy Policy.",
      "By using the service, you acknowledge and agree to those privacy practices.",
    ],
  },
  {
    id: "disclaimer",
    title: "Disclaimer of Warranties",
    paragraphs: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED ON AN \"AS IS\" AND \"AS AVAILABLE\" BASIS. INFRABLAZE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
    ],
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    paragraphs: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, INFRABLAZE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, OR GOODWILL.",
      "INFRABLAZE'S TOTAL AGGREGATE LIABILITY FOR CLAIMS ARISING OUT OF OR RELATED TO THE SERVICE WILL NOT EXCEED THE FEES PAID FOR THE SERVICE IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY, OR USD $100 IF NO FEES WERE PAID.",
    ],
  },
  {
    id: "indemnity",
    title: "Indemnification",
    paragraphs: [
      "You agree to defend, indemnify, and hold harmless Infrablaze and its affiliates from claims arising out of your misuse of the service, breach of these Terms, or violation of applicable law or third-party rights.",
    ],
  },
  {
    id: "termination",
    title: "Term and Termination",
    paragraphs: [
      "These Terms remain in effect while you use the service. We may suspend or terminate access for material breach, legal compliance requirements, or security risk.",
      "Sections that by nature should survive termination will survive, including intellectual property, disclaimers, liability limits, indemnity, and governing law.",
    ],
  },
  {
    id: "compliance",
    title: "Export and Compliance",
    paragraphs: [
      "You agree to comply with applicable export control and sanctions laws and not use the service in prohibited jurisdictions or for prohibited end uses.",
    ],
  },
  {
    id: "law",
    title: "Governing Law and Venue",
    paragraphs: [
      "Unless otherwise agreed in an executed enterprise agreement, these Terms are governed by the laws of the State of Delaware, excluding conflict-of-law rules.",
      "Any dispute arising out of these Terms will be brought in the state or federal courts located in Delaware, and each party consents to personal jurisdiction in those courts.",
    ],
  },
  {
    id: "general",
    title: "General Terms",
    paragraphs: [
      "These Terms, together with incorporated policies and applicable order terms, form the entire agreement regarding use of the service.",
      "If any provision is unenforceable, the remaining provisions remain in effect. Failure to enforce a provision is not a waiver.",
      "You may not assign these Terms without prior written consent, except in connection with a permitted corporate transaction.",
    ],
  },
  {
    id: "contact",
    title: "Contact",
    paragraphs: [
      "For questions about these Terms, contact us at legal@infrablaze.com.",
    ],
  },
];

const TermsOfUsePage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeId, setActiveId] = useState(TERMS_SECTIONS[0].id);
  const [copied, setCopied] = useState("");
  const sectionRefs = useRef({});

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    TERMS_SECTIONS.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <Box component="main" sx={{ flex: 1, display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      <PageMeta
        title="Terms of Service · InfraBlaze"
        description="The terms governing your access to and use of the InfraBlaze, Inc. website and hosted services, including subscriptions, acceptable use, warranties, and liability."
        canonicalPath="/terms-of-use"
      />
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 56px)",
            md: "calc(56px + env(safe-area-inset-top) + 88px)",
          },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.09)}, transparent 60%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Container
          maxWidth="md"
          sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}
        >
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              Terms of Use
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ maxWidth: 640, lineHeight: 1.7, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" } }}
            >
              How you may access and use Infrablaze services. Please read these
              terms carefully.
            </Typography>

            {/* Meta row */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
                }}
              >
                <UpdateIcon sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "primary.main", letterSpacing: 0.3 }}
                >
                  Last updated · {LAST_UPDATED}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/privacy-policy"
                size="small"
                startIcon={<ShieldOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Privacy Policy
              </Button>
              <Button
                component="a"
                href="mailto:legal@infrablaze.com"
                size="small"
                startIcon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Contact legal
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ pb: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
            {/* Scroll-spy Table of Contents */}
            <Grid item xs={12} md={3.5}>
              <Box
                sx={{
                  position: { md: "sticky" },
                  top: { md: 100 },
                  maxHeight: { md: "calc(100vh - 120px)" },
                  overflowY: { md: "auto" },
                  pr: { md: 1 },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    color: "text.secondary",
                    mb: 1.5,
                    fontSize: "0.72rem",
                  }}
                >
                  ON THIS PAGE
                </Typography>
                <Stack
                  spacing={0}
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 4,
                      bottom: 4,
                      width: "2px",
                      background: alpha(theme.palette.divider, 0.8),
                      borderRadius: 2,
                    },
                  }}
                >
                  {TERMS_SECTIONS.map((section) => {
                    const active = activeId === section.id;
                    return (
                      <Box
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          pl: 2,
                          py: 0.7,
                          transition: "all 0.25s ease",
                          borderLeft: "2px solid",
                          borderColor: active
                            ? theme.palette.primary.main
                            : "transparent",
                          ml: "-1px",
                          "&:hover": {
                            "& .toc-label": { color: theme.palette.primary.main },
                          },
                        }}
                      >
                        <Typography
                          className="toc-label"
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: active ? 800 : 600,
                            color: active ? "primary.main" : "text.secondary",
                            lineHeight: 1.45,
                            transition: "color 0.25s ease, font-weight 0.25s ease",
                          }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Grid>

            {/* Document body */}
            <Grid item xs={12} md={8.5}>
              <Box sx={{ maxWidth: 760 }}>
                {TERMS_SECTIONS.map((section, idx) => (
                  <Box
                    key={section.id}
                    id={section.id}
                    ref={(el) => (sectionRefs.current[section.id] = el)}
                    sx={{
                      scrollMarginTop: "90px",
                      pb: { xs: 4, md: 5 },
                      mb: { xs: 4, md: 5 },
                      borderBottom:
                        idx < TERMS_SECTIONS.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : "none",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        "&:hover .copy-link": { opacity: 1 },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 900,
                          letterSpacing: -0.4,
                          lineHeight: 1.2,
                          fontSize: { xs: "1.45rem", md: "1.7rem" },
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Tooltip title={copied === section.id ? "Copied!" : "Copy link"}>
                        <IconButton
                          className="copy-link"
                          size="small"
                          onClick={() => copyLink(section.id)}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.25s ease",
                            color: "text.secondary",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          <LinkIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Stack spacing={2}>
                      {(section.paragraphs || []).map((text, i) => (
                        <Typography
                          key={i}
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.85,
                            fontSize: "1rem",
                          }}
                        >
                          {text}
                        </Typography>
                      ))}
                      {(section.bullets || []).length > 0 ? (
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: 0,
                            listStyle: "none",
                            "& > li": {
                              position: "relative",
                              pl: 3,
                              py: 0.5,
                              color: "text.secondary",
                              lineHeight: 1.8,
                              fontSize: "0.98rem",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                left: 4,
                                top: 14,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.primary.main,
                                boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.4)}`,
                              },
                            },
                          }}
                        >
                          {section.bullets.map((item) => (
                            <Typography key={item} component="li">
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      ) : null}
                    </Stack>
                  </Box>
                ))}

                {/* Bottom contact card */}
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 3, md: 4 },
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.25),
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.5 : 0.92),
                    backdropFilter: "blur(10px)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(400px 150px at 0% 0%, ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}, transparent 60%)`,
                      pointerEvents: "none",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                      Questions about these Terms?
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 2.5, lineHeight: 1.75 }}
                    >
                      Our legal team can help with enterprise agreements, DPAs, or any
                      questions about these Terms.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Button
                        component="a"
                        href="mailto:legal@infrablaze.com"
                        variant="contained"
                        startIcon={<MailOutlineIcon />}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 999,
                          px: 3,
                          textTransform: "none",
                          color: "common.white !important",
                          "&:hover": { color: "common.white !important" },
                        }}
                      >
                        legal@infrablaze.com
                      </Button>
                      <Button
                        component={RouterLink}
                        to="/privacy-policy"
                        variant="outlined"
                        sx={{
                          fontWeight: 900,
                          borderRadius: 999,
                          px: 3,
                          textTransform: "none",
                          borderWidth: 1.5,
                          "&:hover": { borderWidth: 1.5 },
                        }}
                      >
                        View Privacy Policy
                      </Button>
                    </Stack>
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 3, lineHeight: 1.7, fontStyle: "italic", opacity: 0.8 }}
                >
                  This page is provided for general information and product use
                  terms. For enterprise contracts, signed order forms and MSA/DPA
                  terms control where they conflict.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
};

export default TermsOfUsePage;
